<template>
    <div class="modal-backdrop" @click.self="closeModal">
      <div class="relative flex flex-col bg-n-alpha-3 shadow-md modal-container rtl:text-right skip-context-menu rounded-2xl w-[50rem] max-h-[85vh]">
        <div class="flex flex-col py-4 px-6">
          <!-- Two column layout -->
          <div class="flex gap-6">
            <!-- Left column - Instructions -->
            <div class="w-[50%] flex items-center min-h-[400px]">
              <div>
                <p class="text-3xl text-n-slate-12 mb-6">Reconectar Inbox</p>
                <div class="space-y-4">
                  <p class="text-lg text-n-slate-12"><span class="mr-3">1.</span>Abra o WhatsApp no seu celular</p>
                  <p class="text-lg text-n-slate-12"><span class="mr-3">2.</span>Toque em Mais opções no Android ou em Configurações no iPhone</p>
                  <p class="text-lg text-n-slate-12"><span class="mr-3">3.</span>Toque em Dispositivos conectados e, em seguida, em Conectar dispositivo</p>
                  <p class="text-lg text-n-slate-12"><span class="mr-3">4.</span>Escaneie o QR code para confirmar</p>
                  <p class="text-sm text-n-slate-11 mt-6">O código expira após um período de 5 minutos. Fechar esta janela cancelará a conexão.</p>
                </div>
              </div>
            </div>

            <!-- Right column - QR Code -->
            <div class="flex-1 flex items-center justify-center min-h-[400px]">
              <div class="rounded-lg w-[348px] h-[348px] flex items-center justify-center"
                   :class="{ 'bg-n-slate-2': !qrBase64 }">
                <!-- QR Code when available -->
                <div v-if="qrBase64" class="border-4 border-n-slate-4 rounded-lg p-2">
                  <img :src="qrBase64" alt="QR Code" class="max-w-full rounded" />
                </div>
                <!-- Loading indicator when generating QR -->
                <div v-else class="text-center">
                  <span class="animate-spin i-lucide-loader inline-block text-2xl text-n-slate-9"></span>
                  <p class="text-n-slate-9 mt-2">Gerando QR Code...</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Button row below columns -->
          <div class="flex justify-between items-center mt-4">
            <div class="flex items-center gap-3">
              <button type="button"
                  class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border border-n-slate-4 dark:border-n-slate-7 rounded-lg outline-1 outline disabled:opacity-50 bg-transparent text-n-slate-12 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent h-10 px-4 text-sm font-medium justify-center"
                  @click="handleCancelClick">
                  Cancelar
              </button>
              <p v-if="error" class="text-red-500 text-sm">{{ error }}</p>
            </div>
            <button type="button"
                class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-brand text-white outline-transparent h-10 px-4 text-sm font-medium justify-center"
                :disabled="loading">
                <span v-if="loading" class="animate-spin i-lucide-loader"></span>
                <span>{{ loading ? 'Esperando conexão...' : 'Conectado' }}</span>
            </button>
          </div>
        </div>

        <!-- Confirmation dialog overlay for scheduled messages -->
        <div v-if="showDeleteConfirmation && scheduledMessagesCount > 0"
             class="absolute inset-0 bg-black/50 backdrop-blur-sm rounded-2xl flex items-center justify-center z-10">
          <div class="bg-white dark:bg-n-slate-2 rounded-xl shadow-xl p-6 max-w-md mx-4">
            <div class="flex items-start gap-3 mb-4">
              <i class="i-lucide-alert-triangle text-n-amber-9 text-2xl flex-shrink-0"></i>
              <div>
                <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
                  Atenção: Mensagens Agendadas
                </h3>
                <p class="text-n-slate-11 text-sm mb-2">
                  Este inbox possui <strong>{{ scheduledMessagesCount }}</strong> mensagem{{ scheduledMessagesCount > 1 ? 'ns' : '' }} agendada{{ scheduledMessagesCount > 1 ? 's' : '' }}.
                </p>
                <p class="text-n-slate-11 text-sm">
                  Ao cancelar a reconexão e excluir este inbox, todas as mensagens agendadas serão <strong class="text-n-ruby-11">permanentemente excluídas</strong>.
                </p>
              </div>
            </div>
            <div class="flex justify-end gap-3 mt-6">
              <button type="button"
                      class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
                      @click="cancelDelete">
                Voltar
              </button>
              <button type="button"
                      class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-ruby-9 text-white hover:enabled:bg-n-ruby-10 focus-visible:bg-n-ruby-10 outline-transparent h-10 px-4 text-sm font-medium justify-center"
                      @click="confirmDelete"
                      :disabled="loading">
                <span v-if="loading" class="animate-spin i-lucide-loader inline-block mr-2"></span>
                <span>{{ loading ? 'Excluindo...' : 'Confirmar Exclusão' }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
</template>
  
<script>
  import { ref, watch, onBeforeUnmount } from 'vue';
  import { useStore } from 'vuex';
  import { debounce } from 'lodash';
  import wootAPI from '@/v3/api/apiClient';
  
  export default {
    props: {
      inboxName: { type: String, default: '' },
      inboxId: { type: Number, default: 0 },
      show: { type: Boolean, default: false },
      integration_type: { type: String, default: '' }
    },
    emits: ['close', 'save', 'success', 'deleted'],
    setup(props, { emit }) {
      const store = useStore();
      const loading = ref(true);
      const error = ref(null);
      const qrBase64 = ref(null);
      const formData = ref({ name: props.inboxName, instanceName: '' });
      const scheduledMessagesCount = ref(0);
      const showDeleteConfirmation = ref(false);
      let intervalQR = null;
      let intervalStatus = null;
  
      const getCookie = name => {
        const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
        return match ? decodeURIComponent(match[2]) : null;
      };
  
      const getAuthHeaders = () => {
        const cookie = getCookie('cw_d_session_info');
        if (!cookie) return {};
        try {
          const session = JSON.parse(cookie);
          return {
            'access-token': session['access-token'],
            client: session.client,
            uid: session.uid
          };
        } catch {
          return {};
        }
      };
  
      const loadInstanceName = async () => {
        try {
          // Use the Vuex store to get metadata instead of API call
          const metadata = store.getters['inboxMetadata/getInboxMetadata'](props.inboxId);
          if (metadata?.integration_id) {
            formData.value.instanceName = metadata.integration_id;
          } else {
            console.warn(`No integration_id for inbox ${props.inboxId}`);
            // If metadata is not in store, we might need to refresh it
            await store.dispatch('inboxMetadata/loadAllMetadata');
            const updatedMetadata = store.getters['inboxMetadata/getInboxMetadata'](props.inboxId);
            if (updatedMetadata?.integration_id) {
              formData.value.instanceName = updatedMetadata.integration_id;
            }
          }
        } catch (e) {
          error.value = 'Erro ao carregar metadata.';
        }
      };
  
      const fetchInstance = async () => {
        if (!formData.value.instanceName) return;
        
        try {
          loading.value = true;
          // keep loading true until status is open
          const headers = getAuthHeaders();

          const { data } = await wootAPI.get(
            '/api/v1/inboxmeta/fetch_instances',
            { params: { instanceName: formData.value.instanceName }, headers }
          );

          const inst = Array.isArray(data) ? data[0] : null;

          if (inst && inst.connectionStatus === 2) {
            loading.value = false;
            stopPolling();
            // Configure Chatwoot integration upon successful reconnect
            try {
              const configureResponse = await wootAPI.post(
                '/api/v1/inboxmeta/configure_chatwoot',
                {
                  instanceName: formData.value.instanceName,
                  inbox_id: props.inboxId,
                  inbox_name: props.inboxName
                },
                { headers }
              );
              if (configureResponse.data.status !== 'success') {
                console.warn('Failed to configure chatwoot', configureResponse.data);
              }
            } catch (e) {
              console.error('Error configuring chatwoot after reconnect', e);
            }
            emit('success', inst);
          };
        } catch (e) {
          console.error('fetchInstance error', e);
        }
      };
  
      const refreshQR = debounce(async () => {
        error.value = null;
        loading.value = true;
        try {
          const headers = getAuthHeaders();
          const { data, status } = await wootAPI.get(
            '/api/v1/inboxmeta/refresh_qr',
            { params: { instanceName: formData.value.instanceName }, headers }
          );
          if (data.error) {
            error.value = data.error;
            return;
          }
          if (status === 200) {
            let b64 = data.base64;
            if (b64 && !b64.startsWith('data:')) b64 = `data:image/png;base64,${b64.replace(/\s/g, '')}`;
            qrBase64.value = b64;
            if (!formData.value.instanceName && data.instanceName) {
              formData.value.instanceName = data.instanceName;
              emit('save', data);
            }
          }
        } catch (e) {
          error.value = e.response?.data?.error || 'Erro ao atualizar QR code.';
        }
      }, 300, { leading: true, trailing: false });
  
      const startPolling = async () => {
        await loadInstanceName();
        if (!formData.value.instanceName) {
          error.value = `No integration_id for inbox ${props.inboxId}`;
          return;
        }
        // start with loading true
        loading.value = true;
        refreshQR();
        fetchInstance();
        intervalQR = setInterval(refreshQR, 45000);
        intervalStatus = setInterval(fetchInstance, 5000);
      };
  
      const stopPolling = () => {
        clearInterval(intervalQR);
        clearInterval(intervalStatus);
      };
  
      watch(() => props.show, visible => {
        if (visible) startPolling();
        else stopPolling();
      }, { immediate: true });
  
      onBeforeUnmount(stopPolling);
  
      const closeModal = () => {
        stopPolling();
        emit('close');
      };
      
      const handleCancelClick = () => {
        // Show confirmation dialog when user clicks cancel
        showDeleteConfirmation.value = true;
        // Try to delete to check for scheduled messages
        deleteInbox();
      };

      const cancelDelete = () => {
        showDeleteConfirmation.value = false;
        scheduledMessagesCount.value = 0;
      };

      const confirmDelete = () => {
        // Call delete with force
        deleteInbox(true);
      };

      // Added deleteInbox function similar to the one in EditInboxModal.vue
      const deleteInbox = async (forceDelete = false) => {
        loading.value = true;
        error.value = null;

        try {
          const headers = getAuthHeaders();
          const deleteParams = {
            inbox_id: props.inboxId,
            integration_type: props.integration_type || 'evolution'
          };

          // If force delete, add the parameter
          if (forceDelete && scheduledMessagesCount.value > 0) {
            deleteParams.force_delete = true;
          }

          await wootAPI.delete(
            `/api/v1/inboxmeta`,
            {
              params: deleteParams,
              headers,
            }
          );

          // First delete the inbox, then emit deleted event
          stopPolling();
          emit('deleted'); // Emit specific deleted event
        } catch (err) {
          // Check if error is about scheduled messages
          const errorData = err.response?.data;
          const scheduledCount = Number(errorData?.scheduled_messages_count || 0);
          if ((errorData?.requires_confirmation && scheduledCount > 0) || scheduledCount > 0) {
            // Store the count and show confirmation dialog
            scheduledMessagesCount.value = scheduledCount;
            showDeleteConfirmation.value = true;
            loading.value = false;
            return; // Don't close modal, let user see the warning
          }

          // Log the actual error to console for debugging
          const fallbackMessage = errorData?.message || errorData?.error || err.message;
          console.error('Error deleting inbox:', fallbackMessage);

          // If it's a 4xx error, close the modal as if successful
          const statusCode = err.response?.status;
          if (statusCode && statusCode >= 400 && statusCode < 500) {
            stopPolling();
            emit('deleted');
          } else {
            // For other errors, show generic message to user
            error.value = 'Algo deu errado.';
            loading.value = false;
          }
        } finally {
          loading.value = false;
        }
      };
  
      return {
        loading,
        error,
        qrBase64,
        closeModal,
        deleteInbox, // Added deleteInbox to the returned object
        handleCancelClick,
        showDeleteConfirmation,
        scheduledMessagesCount,
        cancelDelete,
        confirmDelete
      };
    }
  };
</script>  
  
<style scoped>
  :root {
      --light-primary-hover: #dddddd;
      --transition-speed: 0.2s;
  }
  
  /* Modal styles */
  .modal-backdrop {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.04);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      backdrop-filter: blur(4px);
  }
  
  .modal-container {
      border-radius: 16px;
      max-width: 95%;
      box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
  }
</style>
